import { useEffect, useState } from 'react'
import { styled } from '@linaria/react'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { CART_ERROR_EVENT } from './cart'

/**
 * A single, app-shell-level listener that turns a `chow:cart:error` window event
 * into a brief toast. Every cart write that can fail emits one — add, the −/+
 * stepper, remove, clear, and checkout — so a failed write says what went wrong
 * instead of the row silently springing back. One instance is mounted globally
 * so no button needs its own error state; the event carries its own title
 * because "couldn't add" is wrong for a removal that failed.
 */
const Dock = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 84px);
  z-index: 60;
  width: min(92vw, 420px);
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`

const DISMISS_MS = 5000

const DEFAULT_TITLE = "Couldn't add to cart"

export const CartErrorToast = () => {
  const [message, setMessage] = useState('')
  const [title, setTitle] = useState(DEFAULT_TITLE)

  useEffect(() => {
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string; title?: string }>).detail
      if (detail?.message) {
        setMessage(detail.message)
        setTitle(detail.title || DEFAULT_TITLE)
      }
    }
    window.addEventListener(CART_ERROR_EVENT, onError)
    return () => window.removeEventListener(CART_ERROR_EVENT, onError)
  }, [])

  useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => setMessage(''), DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [message])

  if (!message) return null
  return (
    <Dock role="status" aria-live="polite">
      <MinimalAlert
        tone="danger"
        title={title}
        action={
          <MinimalButton size="sm" variant="ghost" onClick={() => setMessage('')}>
            Dismiss
          </MinimalButton>
        }
      >
        {message}
      </MinimalAlert>
    </Dock>
  )
}
